import { ReactNode } from 'react';
import { List, ListSubheader } from '@mui/material';
import { MainSidebarItem } from './main-sidebar-item';
import { User } from '../../types/user';
import { services } from '../../services';
import { utils } from '../../utils';

export interface SidebarItem {
  title: string;
  path: string;
  icon?: ReactNode;
  chip?: ReactNode;
  info?: ReactNode;
  children?: SidebarItem[];
  require_scope?: string;
  require_scope2?: string;
  require_role?: string | string[];
  require_feature?: string;
  // Pre-computed gate for a surface whose audience is not a conjunction of the require_* fields
  // above — e.g. "an Acenda staff role OR an organization tag". Computed by the caller, which
  // (unlike this reducer) can call hooks, from the SAME predicate the destination page's guard
  // calls, so the entry can never link to a page the viewer would be denied. `false` hides the
  // item; `undefined` means "no gate", exactly like the fields above — so any gated entry must
  // pass the computed value, not omit it.
  require_access?: boolean;
  isPathExternal?: boolean;
  extra?: ReactNode;
  // Leaf matching is on the organization-relative pathname. A `?key=value` query on `path` must
  // also match; `query_is_default` treats an absent key as matching (e.g. `/import-export` with
  // no `tab` shows the Imports tab). `match_descendants` also matches sub-routes of `path`.
  query_is_default?: boolean;
  match_descendants?: boolean;
}

export interface MainSidebarSectionProps {
  items: SidebarItem[];
  path: string;
  // Omitted for a headerless group (the Dashboard entry above the first titled section).
  title?: string;
  user: User | null;
  require_superuser?: boolean;
  hasFeature?: (feature: string) => boolean;
  [key: string]: any;
}

type NavGates = {
  hasScope: (scope: string) => boolean;
  hasFeature: (feature: string) => boolean;
};

// `path` is the router's asPath: `/{organization}/{route}/?query#hash`.
const parseLocation = (path: string) => {
  const [withQuery] = path.split('#', 1);
  const [pathname, query = ''] = withQuery.split('?', 2);
  const segments = pathname.split('/').filter(Boolean);
  return { route: '/' + segments.slice(1).join('/'), query: new URLSearchParams(query) };
};

export const isNavItemActive = (item: SidebarItem, path: string): boolean => {
  if (item.isPathExternal) {
    return false;
  }
  const location = parseLocation(path);
  const [itemPathname, itemQuery = ''] = item.path.split('?', 2);
  const itemRoute = itemPathname.replace(/\/+$/g, '') || '/';

  const routeMatches =
    location.route === itemRoute || (item.match_descendants === true && itemRoute !== '/' && location.route.startsWith(`${itemRoute}/`));
  if (!routeMatches) {
    return false;
  }

  return Array.from(new URLSearchParams(itemQuery).entries()).every(([key, value]) => {
    const actual = location.query.get(key);
    return actual === null ? item.query_is_default === true : actual === value;
  });
};

const isNavItemVisible = (item: SidebarItem, { hasScope, hasFeature }: NavGates): boolean => {
  if (item.require_scope && !hasScope(item.require_scope) && !utils.authUtils.isAcendaStaff()) {
    return false;
  }
  if (item.require_scope2 && !hasScope(item.require_scope2) && !utils.authUtils.isAcendaStaff()) {
    return false;
  }

  if (item.require_feature && !hasFeature(item.require_feature)) {
    return false;
  }

  // Set membership against every realm role the user holds. Resolving one staff role and
  // comparing is order-dependent (the snapshot is sorted) and dropped the management-gated
  // items for anyone who also held a second acenda_* role. Fail closed for non-staff too.
  if (item.require_role && !utils.authUtils.hasRealmRole(item.require_role)) {
    return false;
  }

  if (item.require_access === false) {
    return false;
  }

  // A branch whose children are all gated out would be an empty, useless toggle.
  if (item.children) {
    return item.children.some((child) => isNavItemVisible(child, { hasScope, hasFeature }));
  }

  return true;
};

// A branch is active (and opened) when any child the viewer can see is the current route; its own
// `path` is never matched, so branch paths are non-route sentinels.
const isBranchActive = (item: SidebarItem, path: string, gates: NavGates): boolean =>
  (item.children ?? []).some(
    (child) => isNavItemVisible(child, gates) && (child.children ? isBranchActive(child, path, gates) : isNavItemActive(child, path))
  );

const renderNavItems = ({ depth = 0, items, path, gates }: { depth?: number; items: SidebarItem[]; path: string; gates: NavGates }) => (
  <List key={`nav-list-${depth}`} disablePadding>
    {items.reduce((acc, item) => reduceChildRoutes({ acc, item, depth, path, gates }), [] as ReactNode[])}
  </List>
);

const reduceChildRoutes = ({
  acc,
  item,
  depth,
  path,
  gates,
}: {
  acc: ReactNode[];
  item: SidebarItem;
  depth: number;
  path: string;
  gates: NavGates;
}) => {
  if (!isNavItemVisible(item, gates)) {
    return acc;
  }

  const key = `${item.title}-${depth}`;

  if (item.children) {
    const active = isBranchActive(item, path, gates);
    acc.push(
      <MainSidebarItem
        active={active}
        chip={item.chip}
        depth={depth}
        icon={item.icon}
        info={item.info}
        key={key}
        open={active}
        path={item.path}
        isPathExternal={item.isPathExternal}
        title={item.title}
        extra={item.extra}
      >
        {renderNavItems({
          depth: depth + 1,
          items: item.children,
          path,
          gates,
        })}
      </MainSidebarItem>
    );
    return acc;
  }

  acc.push(
    <MainSidebarItem
      active={isNavItemActive(item, path)}
      chip={item.chip}
      depth={depth}
      icon={item.icon}
      info={item.info}
      key={key}
      path={item.path}
      isPathExternal={item.isPathExternal}
      title={item.title}
      extra={item.extra}
    />
  );

  return acc;
};

export const MainSidebarSection = (props: MainSidebarSectionProps) => {
  const { items, path, title, user, require_superuser, hasFeature = () => false, ...other } = props;

  const hasScope = (scope: string) => services.user.userHasScope(user, scope);
  const gates: NavGates = { hasScope, hasFeature };

  return (
    <List
      subheader={
        title ? (
          <ListSubheader
            disableGutters
            disableSticky
            sx={{
              color: 'neutral.500',
              fontSize: '0.75rem',
              fontWeight: 700,
              lineHeight: 2.5,
              ml: 4,
              textTransform: 'uppercase',
            }}
          >
            {title}
          </ListSubheader>
        ) : undefined
      }
      {...other}
    >
      {renderNavItems({
        items,
        path,
        gates,
      })}
    </List>
  );
};
